import { createElement, Fragment, useEffect, useRef } from 'react'
import { createRoot, type Root } from 'react-dom/client'
import { useLocation, useNavigate } from 'react-router-dom'
import { autocomplete } from '@algolia/autocomplete-js'
import { isEmpty } from 'lodash-es'

import { useBasePageUrl } from 'Hooks'

import { getQueryObject, mergeToQueryString } from 'Utilities'

import { GLOBAL_SEARCH_MIN_QUERY_LENGTH } from './GlobalSearch.constants'
import {
  getGlobalSearchNavigationTarget,
  isGlobalSearchSubmissionItem,
} from './GlobalSearch.utils'

export const GlobalSearchAutocomplete = (props: any) => {
  const { isInitiallyOpen, initialCollection, ...restProps } = props
  const autocompleteRef = useRef(null)
  const panelRootRef = useRef<Root | null>(null)
  const rootRef = useRef<HTMLElement | null>(null)
  const navigate = useNavigate()
  const { search: routerSearch } = useLocation()
  const { basePageUrl } = useBasePageUrl()

  useEffect(() => {
    if (!autocompleteRef.current) {
      return undefined
    }

    const currentSearchQuery = getQueryObject(routerSearch)

    const search = autocomplete({
      classNames: {
        panel: 'gs-panel',
        panelLayout: 'gs-panel-layout',
        submitButton: 'gs-submit-button',
      },
      container: autocompleteRef.current,
      detachedMediaQuery: 'none',
      getInputProps({ props }) {
        const { onKeyDown: handleKeyDown } = props

        return {
          ...props,
          onKeyDown(event: KeyboardEvent) {
            handleKeyDown(event)

            if (
              event.key !== 'Enter' ||
              event.defaultPrevented ||
              !(event.currentTarget instanceof HTMLInputElement)
            ) {
              return
            }

            // Native implicit submission can be skipped after an empty response.
            // Route Enter through the same submit control as the search icon.
            event.preventDefault()
            event.currentTarget.form
              ?.querySelector<HTMLButtonElement>('.gs-submit-button')
              ?.click()
          },
          tabIndex: 2,
        }
      },
      initialState: {
        collections: isEmpty(initialCollection) ? [] : [initialCollection],
        isOpen: isInitiallyOpen,
        query: currentSearchQuery?.searchTerm || '',
      },
      onReset: () => {
        const clearedSearchQuery = mergeToQueryString(routerSearch, {
          searchTerm: '',
        })

        navigate({ search: clearedSearchQuery }, { replace: true })
      },
      onSubmit: params => {
        const { state } = params
        const trimmedQuery = state.query.trim()

        if (trimmedQuery.length < GLOBAL_SEARCH_MIN_QUERY_LENGTH) {
          return
        }

        const activeItem: unknown = state.collections
          .map(collectionItem =>
            collectionItem.items.find(
              item => item.__autocomplete_id === state.activeItemId,
            ),
          )
          .filter(item => item !== undefined)
          .flat()[0]
        const selectedItem = isGlobalSearchSubmissionItem(activeItem)
          ? activeItem
          : undefined

        navigate(
          getGlobalSearchNavigationTarget({
            basePageUrl,
            query: trimmedQuery,
            routerSearch,
            selectedItem,
          }),
          { replace: true },
        )
      },
      render: ({ children }, root) => {
        if (!panelRootRef.current || rootRef.current !== root) {
          rootRef.current = root

          panelRootRef.current?.unmount()
          panelRootRef.current = createRoot(root)
        }

        panelRootRef?.current?.render(children)
      },
      renderer: { Fragment, createElement, render: () => {} },
      shouldPanelOpen: () => true,
      stallThreshold: 1500,
      ...restProps,
    })

    return () => {
      search.destroy()
    }
  }, [
    restProps,
    isInitiallyOpen,
    initialCollection,
    routerSearch,
    basePageUrl,
    navigate,
  ])

  return <div ref={autocompleteRef} className="gs-autocomplete w-100" />
}
